import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsPerson, timeAgo } from './api';
import { inputCls } from './shared';

// One person record underneath (candidate/contact are role flags on the same
// row — a contact who becomes a candidate keeps one history), but the UI
// presents them the traditional way: Candidates under ATS (delivery),
// Contacts under CRM (business development), each with its own columns.

const AtsPeople: React.FC<{ mode: 'candidate' | 'contact' }> = ({ mode }) => {
  const { token } = useAuth();
  const [people, setPeople] = useState<AtsPerson[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const qs = new URLSearchParams({ role: mode });
    if (search) qs.set('search', search);
    atsFetch(token, `/people?${qs}`)
      .then((d) => setPeople(d.people))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, search, mode]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load, search]);
  useEffect(() => { setSelected(new Set()); }, [mode]);

  const isCandidates = mode === 'candidate';
  const other = isCandidates ? 'contact' : 'candidate';
  const allSelected = people.length > 0 && people.every((p) => selected.has(p.id));

  const toggle = (id: number, on: boolean) => {
    setSelected((prev) => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });
  };

  const bulkDelete = async () => {
    if (!token || !selected.size || deleting) return;
    const n = selected.size;
    if (!window.confirm(
      `Delete ${n} ${isCandidates ? 'candidate' : 'contact'}${n === 1 ? '' : 's'}?\n\n` +
      `• Anyone who is ALSO a ${other} keeps their record — only the ${mode} role ${isCandidates ? 'and their pipeline cards' : 'and their client links'} are removed.\n` +
      `• Everyone else is permanently deleted${isCandidates ? ' along with their pipeline cards' : ''}.\n` +
      `• Placement records are kept; synced emails stay in the Inbox as unlinked threads.\n\nThis cannot be undone.`
    )) return;
    setDeleting(true);
    try {
      await atsFetch(token, '/people/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: Array.from(selected), mode }) });
      setSelected(new Set());
      load();
    } catch { /* table reloads on next action */ }
    setDeleting(false);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${isCandidates ? 'candidates' : 'contacts'}…`} className={`${inputCls} max-w-xs`} />
        {selected.size > 0 && (
          <span className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <button onClick={bulkDelete} disabled={deleting} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
      ) : people.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 mb-1">No {isCandidates ? 'candidates' : 'contacts'} yet.</p>
          <p className="text-xs text-gray-400">
            {isCandidates
              ? 'Candidates arrive here when you add them to a role’s pipeline — your mapped market stays separate by design.'
              : 'Contacts are the client-side people (hiring managers, HR) — add them from a client record.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="pl-4 pr-1 py-2.5 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => setSelected(e.target.checked ? new Set(people.map((p) => p.id)) : new Set())}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-2 py-2.5 font-medium">Name</th>
                {isCandidates ? (
                  <>
                    <th className="px-3 py-2.5 font-medium">Current position</th>
                    <th className="px-3 py-2.5 font-medium">Location</th>
                    <th className="px-3 py-2.5 font-medium">Email</th>
                    <th className="px-3 py-2.5 font-medium text-center">Pipelines</th>
                  </>
                ) : (
                  <>
                    <th className="px-3 py-2.5 font-medium">Job title</th>
                    <th className="px-3 py-2.5 font-medium">Client</th>
                    <th className="px-3 py-2.5 font-medium">Email</th>
                    <th className="px-3 py-2.5 font-medium">Phone</th>
                  </>
                )}
                <th className="px-5 py-2.5 font-medium text-right">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.id} className={`border-b border-gray-50 last:border-0 hover:bg-gray-50/60 ${selected.has(p.id) ? 'bg-red-50/40' : ''}`}>
                  <td className="pl-4 pr-1 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={(e) => toggle(p.id, e.target.checked)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-2 py-3">
                    <Link to={`/ats/people/${p.id}`} className="font-medium text-gray-900 hover:text-blue-700">{p.name}</Link>
                    {p.is_candidate && p.is_contact && (
                      <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 text-gray-500 uppercase" title="This person is both a candidate and a client contact">Both</span>
                    )}
                  </td>
                  {isCandidates ? (
                    <>
                      <td className="px-3 py-3 text-gray-500 text-xs max-w-[240px] truncate">{[p.title, p.company].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="px-3 py-3 text-gray-500 text-xs">{p.location || '—'}</td>
                      <td className="px-3 py-3 text-gray-500 text-xs">{p.email || '—'}</td>
                      <td className="px-3 py-3 text-center text-gray-700">{p.active_cards || 0}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-3 text-gray-500 text-xs">{p.title || '—'}</td>
                      <td className="px-3 py-3 text-gray-600 text-xs">{p.client_names || p.company || '—'}</td>
                      <td className="px-3 py-3 text-gray-500 text-xs">{p.email || '—'}</td>
                      <td className="px-3 py-3 text-gray-500 text-xs">{p.phone || '—'}</td>
                    </>
                  )}
                  <td className="px-5 py-3 text-right text-xs text-gray-400">{p.last_activity ? timeAgo(p.last_activity) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AtsPeople;
